import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import useQueryPagination from 'hooks/useQueryPagination'
import { AppDispatch, RootState } from 'store'
import { Flex, MainHeadline, PageFooter, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import {
  approveInventoryStockTake,
  clearInventoryAdjustments,
  clearInventoryStockTakes,
  countInventoryStockTake,
  createInventoryAdjustment,
  createInventoryStockTake,
  getInventoryAdjustments,
  getInventoryCountSchedules,
  getInventoryStockTake,
  getInventoryStockTakes,
  postInventoryAdjustment,
  startInventoryStockTake,
  submitInventoryStockTake,
} from 'features/inventory/inventoryControlSlice'
import {
  IInventoryAdjustmentResponse,
  IInventoryCountScheduleResponse,
  IInventoryStockTakeResponse,
} from 'types/api/inventoryControlApi.interface'
import {
  IInventoryAdjustmentCreateState,
  IInventoryStockTakeCreateState,
  IInventoryStockTakesTab,
} from 'types/inventoryControl.interface'
import {
  InventoryAdjustmentReason,
  InventoryAdjustmentStatus,
  InventoryChargeToKind,
  InventoryCountCadence,
  InventoryStockTakeStatus,
} from 'types/inventoryEnums.interface'
import { formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import {
  ADJUSTMENT_REASON_LABEL,
  ADJUSTMENT_STATUS_META,
  CHARGE_TO_KIND_LABEL,
  COUNT_CADENCE_LABEL,
  COUNT_SCHEDULE_STATE_META,
  STOCK_TAKE_STATUS_META,
} from '../Shared/inventoryEnumMaps'
import {
  countProgressLabel,
  signedGhs,
  stockTakeVarianceTone,
} from '../Shared/inventoryFormatters'
import { StockTakeCreateModal } from './StockTakeCreateModal'
import { StockTakeDetail } from './StockTakeDetail'
import { InventoryAdjustmentModal } from './InventoryAdjustmentModal'
import { CellValue, SheetNoteText, VarianceValue } from './InventoryStockTakes.styled'

const STOCK_TAKES_TABS: IInventoryStockTakesTab[] = [
  { id: 'counts', label: 'Counts' },
  { id: 'adjustments', label: 'Adjustments' },
]

export const InventoryStockTakes = () => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    stockTakes,
    singleStockTake,
    adjustments,
    countSchedules,
    actionLoading,
    isLoading,
  } = useSelector((store: RootState) => store.inventoryControl)

  const [tab, setTab] = useState<IInventoryStockTakesTab['id']>('counts')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [createModal, setCreateModal] = useState(false)
  const [adjustmentModal, setAdjustmentModal] = useState(false)
  const [params, setParams] = useState<any>(getParams({ location, limit: 30 }))
  const [adjParams, setAdjParams] = useState<any>(getParams({ location, limit: 30 }))

  useQueryPagination({
    fetchFunctions: [getInventoryStockTakes, getInventoryCountSchedules],
    cleanUpFunctions: [clearInventoryStockTakes],
    params,
  })

  useQueryPagination({
    fetchFunctions: [getInventoryAdjustments],
    cleanUpFunctions: [clearInventoryAdjustments],
    params: adjParams,
  })

  const rows = stockTakes.data ?? []
  const adjustmentRows = adjustments.data ?? []
  const scheduleRows = countSchedules.data ?? []

  const selected = useMemo(() => {
    if (selectedId == null) return null
    if (singleStockTake?.id === selectedId) return singleStockTake
    return rows.find((row) => row.id === selectedId) || null
  }, [selectedId, singleStockTake, rows])

  const openDetail = async (id: number) => {
    setSelectedId(id)
    await dispatch(getInventoryStockTake(id))
  }

  const handleCreate = async (state: IInventoryStockTakeCreateState) => {
    const resp = await dispatch(
      createInventoryStockTake({
        name: state.name.trim(),
        locationId: Number(state.location.value),
        scheduledDate: state.scheduledDate.toISOString(),
        blind: state.blind,
        cadence: state.cadence?.value != null ? Number(state.cadence.value) : undefined,
      }),
    )
    if (resp.meta.requestStatus === 'fulfilled') {
      toast.success('Stock take scheduled')
    }
  }

  const handleCreateAdjustment = async (state: IInventoryAdjustmentCreateState) => {
    const resp = await dispatch(
      createInventoryAdjustment({
        reason: Number(state.reason.value) as InventoryAdjustmentReason,
        locationId: Number(state.location.value),
        lines: [
          {
            itemId: Number(state.item.value),
            qtyDelta: Number(state.quantity),
            note: state.note.trim() || undefined,
          },
        ],
        chargeTo: {
          kind: Number(state.chargeToKind.value) as InventoryChargeToKind,
          label: state.chargeToLabel.trim(),
        },
        note: state.note.trim() || undefined,
      }),
    )
    if (resp.meta.requestStatus === 'fulfilled') {
      toast.success('Adjustment recorded — OPEN, awaiting post')
    }
  }

  const handleCount = (row: IInventoryStockTakeResponse) => async (
    line: { id: number },
    countedQty: number,
  ) => {
    const resp = await dispatch(
      countInventoryStockTake({
        id: row.id,
        body: { lines: [{ lineId: line.id, countedQty }] },
      }),
    )
    if (resp.meta.requestStatus === 'fulfilled') {
      toast.success(`Counted line ${line.id} — ${countedQty}`)
    }
  }

  const handleStart = async (row: IInventoryStockTakeResponse) => {
    await dispatch(startInventoryStockTake(row.id))
  }

  const handleSubmitForApproval = async (row: IInventoryStockTakeResponse) => {
    await dispatch(submitInventoryStockTake(row.id))
  }

  const handleApprove = async (row: IInventoryStockTakeResponse) => {
    await dispatch(approveInventoryStockTake(row.id))
  }

  const handlePostAdjustment = async (row: IInventoryAdjustmentResponse) => {
    if (row.status !== InventoryAdjustmentStatus.Open) return
    await dispatch(postInventoryAdjustment(row.id))
  }

  const columns: IHonestTableColumn<IInventoryStockTakeResponse>[] = [
    { id: 'id', header: 'ID', cell: (row) => <CellValue $em>{`ST-${row.id}`}</CellValue> },
    { id: 'name', header: 'NAME', minWidth: '16rem', cell: (row) => row.name },
    {
      id: 'location',
      header: 'LOCATION',
      cell: (row) => row.location?.name ?? '—',
    },
    {
      id: 'scheduled',
      header: 'SCHEDULED / STARTED',
      cell: (row) => {
        const scheduled = row.scheduledDate ? format(new Date(row.scheduledDate), 'd MMM') : '—'
        const started = row.startedAt ? format(new Date(row.startedAt), 'd MMM') : null
        return <CellValue>{`${scheduled}${started ? ` / ${started}` : ''}`}</CellValue>
      },
    },
    {
      id: 'counted',
      header: 'COUNTED LINES',
      align: 'right',
      cell: (row) => {
        const counted = (row.lines ?? []).filter((line) => line.countedQty != null).length
        const total = row.lines?.length ?? null
        return <CellValue>{countProgressLabel(counted, total)}</CellValue>
      },
    },
    {
      id: 'variance',
      header: 'VARIANCE',
      align: 'right',
      cell: (row) => {
        const lines = row.lines ?? []
        if (!lines.length || lines.every((line) => line.varianceGhs == null)) {
          return <OtherText grey>—</OtherText>
        }
        const variance = lines.reduce((sum, line) => sum + (line.varianceGhs ?? 0), 0)
        const sheetValue = lines.reduce(
          (sum, line) => sum + Math.abs((line.systemQty ?? 0) * (line.unitCost ?? 0)),
          0,
        )
        const danger = stockTakeVarianceTone(variance, sheetValue) === 'danger'
        return (
          <VarianceValue $tone={danger ? 'danger' : variance > 0 ? 'positive' : 'normal'}>
            {signedGhs(variance)}
          </VarianceValue>
        )
      },
    },
    {
      id: 'status',
      header: 'STATUS',
      cell: (row) => {
        const meta = STOCK_TAKE_STATUS_META[row.status] ?? { label: String(row.status), tone: 'neutral' as const }
        return (
          <StatusPill
            status={meta.tone}
            dot={row.status === InventoryStockTakeStatus.AwaitingApproval}
          >
            {meta.label}
          </StatusPill>
        )
      },
    },
  ]

  const scheduleColumns: IHonestTableColumn<IInventoryCountScheduleResponse>[] = [
    {
      id: 'location',
      header: 'LOCATION',
      minWidth: '12rem',
      cell: (row) => <CellValue $em>{row.location?.name ?? '—'}</CellValue>,
    },
    {
      id: 'cadence',
      header: 'CADENCE',
      minWidth: '10rem',
      cell: (row) => (
        <CellValue>{COUNT_CADENCE_LABEL[row.cadence] ?? String(row.cadence)}</CellValue>
      ),
    },
    { id: 'scope', header: 'SCOPE', cell: (row) => <CellValue>{row.scope}</CellValue> },
    {
      id: 'lastDone',
      header: 'LAST DONE',
      cell: (row) => (
        <CellValue>{row.lastDoneAt ? formatDateForDisplay(row.lastDoneAt) : '—'}</CellValue>
      ),
    },
    {
      id: 'nextDue',
      header: 'NEXT DUE',
      minWidth: '10rem',
      cell: (row) => {
        const meta = COUNT_SCHEDULE_STATE_META[row.state]
        const next = row.nextDueAt ? formatDateForDisplay(row.nextDueAt) : '—'
        if (meta?.tone === 'danger') {
          return <StatusPill status='danger' dot>{`Overdue — ${next}`}</StatusPill>
        }
        if (meta?.tone === 'accent') {
          return <StatusPill status='info'>Counting now</StatusPill>
        }
        return <CellValue>{next}</CellValue>
      },
    },
    {
      id: 'owner',
      header: 'OWNER',
      cell: (row) => <OtherText grey>{row.owner?.name ?? '—'}</OtherText>,
    },
  ]

  const adjustmentColumns: IHonestTableColumn<IInventoryAdjustmentResponse>[] = [
    {
      id: 'ref',
      header: 'REF',
      cell: (row) => <CellValue $em>{row.ref ?? `ADJ-${row.id}`}</CellValue>,
    },
    {
      id: 'date',
      header: 'DATE',
      cell: (row) => <CellValue>{formatDateForDisplay(row.date)}</CellValue>,
    },
    {
      id: 'reason',
      header: 'REASON',
      cell: (row) => (
        <StatusPill
          status={row.reason === InventoryAdjustmentReason.Theft ? 'danger' : 'neutral'}
          dot={row.reason === InventoryAdjustmentReason.Theft}
        >
          {ADJUSTMENT_REASON_LABEL[row.reason] ?? String(row.reason)}
        </StatusPill>
      ),
    },
    {
      id: 'items',
      header: 'ITEMS',
      minWidth: '14rem',
      cell: (row) =>
        row.lines?.length
          ? row.lines.map((line) => `#${line.inventoryItemId} Δ${line.qtyDelta}`).join(', ')
          : '—',
    },
    {
      id: 'value',
      header: 'VALUE',
      align: 'right',
      cell: (row) => (
        <VarianceValue
          $tone={row.valueGhs < 0 ? 'danger' : row.valueGhs > 0 ? 'positive' : 'normal'}
        >
          {signedGhs(row.valueGhs)}
        </VarianceValue>
      ),
    },
    {
      id: 'chargedTo',
      header: 'CHARGED TO',
      minWidth: '12rem',
      cell: (row) => {
        if (row.chargeToKind == null) return <OtherText grey>—</OtherText>
        const kind =
          CHARGE_TO_KIND_LABEL[row.chargeToKind] ?? String(row.chargeToKind)
        return <CellValue>{`${kind} · ${row.chargeToLabel ?? '—'}`}</CellValue>
      },
    },
    {
      id: 'status',
      header: 'STATUS',
      cell: (row) => {
        const meta = ADJUSTMENT_STATUS_META[row.status] ?? {
          label: String(row.status),
          tone: 'neutral' as const,
        }
        return (
          <StatusPill status={meta.tone} dot={row.status === InventoryAdjustmentStatus.Open}>
            {meta.label}
          </StatusPill>
        )
      },
    },
    {
      id: 'note',
      header: 'NOTE',
      minWidth: '12rem',
      cell: (row) =>
        row.note ? <SheetNoteText>{row.note}</SheetNoteText> : <OtherText grey>—</OtherText>,
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) =>
        row.status === InventoryAdjustmentStatus.Open ? (
          <Button
            size='sm'
            variant='secondary'
            onClick={(e) => {
              e.stopPropagation()
              handlePostAdjustment(row)
            }}
          >
            Post
          </Button>
        ) : (
          <OtherText grey>—</OtherText>
        ),
    },
  ]

  return (
    <>
      <NavBar />
      <PageWrapper>
        {selected ? (
          <StockTakeDetail
            stockTake={selected}
            onCount={handleCount(selected)}
            onStart={handleStart}
            onSubmitForApproval={handleSubmitForApproval}
            onApprove={handleApprove}
            onBack={() => setSelectedId(null)}
            actionLoading={actionLoading || isLoading}
          />
        ) : (
          <>
            <Flex between center mb='16'>
              <MainHeadline>Stock takes</MainHeadline>
              {tab === 'counts' ? (
                <Button variant='primary' onClick={() => setCreateModal(true)}>
                  New stock take
                </Button>
              ) : (
                <Button variant='primary' onClick={() => setAdjustmentModal(true)}>
                  New adjustment
                </Button>
              )}
            </Flex>
            <Flex mb='16'>
              <TabsContainer>
                {STOCK_TAKES_TABS.map((t) => (
                  <Tabs key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
                    {t.label}
                  </Tabs>
                ))}
              </TabsContainer>
            </Flex>
            {tab === 'counts' ? (
              <>
                {scheduleRows.length > 0 && (
                  <Flex mb='16'>
                    <Container>
                      <ContainerHeader>
                        <Flex between center width='100%'>
                          <TitleText>Count schedule</TitleText>
                          <Button size='sm' variant='secondary' onClick={() => setCreateModal(true)}>
                            Schedule count
                          </Button>
                        </Flex>
                      </ContainerHeader>
                      <ContainerContent>
                        <HonestTable
                          columns={scheduleColumns}
                          data={scheduleRows}
                          keyField='id'
                        />
                      </ContainerContent>
                    </Container>
                  </Flex>
                )}
                {!rows.length ? (
                  <NoItems title='Stock takes' />
                ) : (
                  <HonestTable
                    columns={columns}
                    data={rows}
                    keyField='id'
                    onClick={(row) => openDetail(row.id)}
                  />
                )}
                <PageFooter>
                  <Pagination
                    setPage={(page) => handlePagination(page, setParams)}
                    pageCount={Math.max(stockTakes.totalPages || 0, 1)}
                    forcePage={(params.page || 1) - 1}
                  />
                </PageFooter>
              </>
            ) : (
              <>
                {!adjustmentRows.length ? (
                  <NoItems title='Adjustments' />
                ) : (
                  <HonestTable columns={adjustmentColumns} data={adjustmentRows} keyField='id' />
                )}
                <PageFooter>
                  <Pagination
                    setPage={(page) => handlePagination(page, setAdjParams)}
                    pageCount={Math.max(adjustments.totalPages || 0, 1)}
                    forcePage={(adjParams.page || 1) - 1}
                  />
                </PageFooter>
              </>
            )}
          </>
        )}
        {createModal && (
          <StockTakeCreateModal
            closeModal={() => setCreateModal(false)}
            onCreate={handleCreate}
          />
        )}
        {adjustmentModal && (
          <InventoryAdjustmentModal
            closeModal={() => setAdjustmentModal(false)}
            onCreate={handleCreateAdjustment}
          />
        )}
      </PageWrapper>
    </>
  )
}
